import { VideoHandlerBase, VideoHandlerConfig } from './Abstract';
import { loadYouTubeIframeAPI } from '../../Utility/YouTube';
import { Log } from '../../AuxillaryService/Log';
import _ from 'underscore';
import { Media, State } from './types';
import { extractYouTubeVideoId } from '../Helpers/YouTubeHelper';

export type YouTubeHandlerConfig = VideoHandlerConfig & {
    youtubeLink: string;
    youtubePlaybackQuality?: YT.SuggestedVideoQuality;
    showSubtitles?: boolean;
};

const INVALID_VIDEO_ID = 'INVALID_VIDEO_ID';
const validQualities = ['small', 'medium', 'large', 'hd720', 'hd1080', 'highres'];

function ytErrorMessage(errCode: YT.PlayerError): string {
    let message = 'Unknown Error.';
    switch (errCode) {
        case 2: // InvalidParam
            message = 'The request contained an invalid parameter value.';
            break;
        case 5: // Html5Error
            message = 'The requested content cannot be played in an HTML5 player.';
            break;
        case 100: // VideoNotFound
            message = 'The video requested was not found.';
            break;
        case 101: // EmbeddingNotAllowed
        case 150: // EmbeddingNotAllowed2
            message =
                'The owner of the requested video does not allow it to be played in embedded players.';
            break;
    }
    return message;
}

function ytPlayerState(state: number): string {
    let stateString = 'UNKNOWN';
    switch (state) {
        case -1:
            stateString = 'UNSTARTED';
            break;
        case YT.PlayerState.ENDED:
            stateString = 'ENDED';
            break;
        case YT.PlayerState.PLAYING:
            stateString = 'PLAYING';
            break;
        case YT.PlayerState.PAUSED:
            stateString = 'PAUSED';
            break;
        case YT.PlayerState.BUFFERING:
            stateString = 'BUFFERING';
            break;
        case YT.PlayerState.CUED:
            stateString = 'CUED';
            break;
    }

    return stateString;
}

export class YouTubeHandler extends VideoHandlerBase<YT.Player> {
    protected _type: string;
    protected _videoId: string;
    protected _playbackQuality?: YT.SuggestedVideoQuality;
    protected _showSubtitles: boolean;
    protected _onYouTubeReady: Promise<void>;

    constructor(config: YouTubeHandlerConfig) {
        super(config);
        try {
            this._videoId = extractYouTubeVideoId(config.youtubeLink);
        } catch (ex) {
            //Swallow error in order to avoid throwing
            this._videoId = INVALID_VIDEO_ID;
        }
        this._onYouTubeReady = loadYouTubeIframeAPI();
        this._type = 'youtube';

        if (
            config.youtubePlaybackQuality &&
            validQualities.includes(config.youtubePlaybackQuality)
        ) {
            this._playbackQuality = config.youtubePlaybackQuality;
        }
        this._showSubtitles = config.showSubtitles || false;
    }

    protected async _createNewPlayer(): Promise<YT.Player> {
        if (this._videoId === INVALID_VIDEO_ID) {
            throw new Error('Valid Video ID not found.');
        }

        // this element cannot be used as the player element, because YT
        // destroys it. we'll need to create a new element for the YT player.
        const element = this.getElement();
        const playerElement = document.createElement('div');
        element.appendChild(playerElement);

        await this._onYouTubeReady;

        const player = new YT.Player(playerElement, {
            width: element?.offsetWidth,
            height: element?.offsetHeight,
            videoId: this._videoId,
            playerVars: {
                autohide: 1,
                autoplay: 0,
                controls: this._showControls ? 1 : 0,
                enablejsapi: 1,
                rel: 0,
                showinfo: 0,
                cc_load_policy: this._showSubtitles ? 1 : 0,
            },
        });

        await new Promise<void>((resolve, reject) => {
            player.addEventListener(
                'onReady',
                _.once((event: YT.PlayerEvent) => {
                    Log.Debug(`[${this.id}] Player Ready.`, null, __fileline, event);

                    resolve();
                })
            );

            player.addEventListener(
                'onError',
                _.once((event: YT.OnErrorEvent) => {
                    const errCode = event.data;
                    const message = ytErrorMessage(errCode);
                    reject(new Error(`${message} [${errCode}]`));
                })
            );
        });

        player.addEventListener('onError', (event: YT.OnErrorEvent) => {
            const errCode = event.data;
            const message = ytErrorMessage(errCode);
            Log.Error(`[${this.id}] Error: ${message} [${errCode}]`, null, __fileline);
        });

        player.addEventListener('onStateChange', (event: YT.OnStateChangeEvent) => {
            const state = ytPlayerState(event.data);
            Log.Debug(`[${this.id}] State changed to ${state}.`, null, __fileline, event);

            switch (event.data) {
                case YT.PlayerState.ENDED:
                    this.eventEmitter.emitEvent(Media.Ended);
                    break;
            }
        });

        player.addEventListener(
            'onPlaybackQualityChange',
            (event: YT.OnPlaybackQualityChangeEvent) => {
                const playbackQuality = event.data;
                Log.Debug(
                    `[${this.id}] Playback quality changed to '${playbackQuality}'.`,
                    null,
                    __fileline,
                    event
                );
            }
        );

        player.addEventListener('onPlaybackRateChange', (event: YT.OnPlaybackRateChangeEvent) => {
            const playbackRate = String(event.data);
            Log.Debug(
                `[${this.id}] Playback rate changed to ${playbackRate}x.`,
                null,
                __fileline,
                event
            );
        });

        if (this._playbackQuality) {
            player.setPlaybackQuality(this._playbackQuality);
        }

        if (this._muted) {
            player.mute();
        }

        return player;
    }

    protected async _setCurrentTime(seconds: number): Promise<void> {
        const player = this._getAttachedPlayer();
        player.seekTo(seconds, true);
        if (this._state !== State.PLAYING) {
            // `seekTo` may start playing the video is it isn't already,
            // so we want to make sure the video stays paused/stopped.
            player.pauseVideo();
        }
    }

    protected async _doPlay(): Promise<void> {
        const player = this._getAttachedPlayer();
        player.playVideo();
    }

    protected async _doPause(): Promise<void> {
        const player = this._getAttachedPlayer();
        player.pauseVideo();
    }

    protected async _doDestroyPlayer(): Promise<void> {
        const player = this._getPossiblyDetachedPlayer();
        player.stopVideo();
        player.destroy();
    }
}
